import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Leaderboard list - light Mundalo theme. Used by:
 *   • /portal/community/leaderboard (limit=20)
 *   • right-rail sidebar on /portal/community (limit=10)
 *
 * Scope:
 *   • `scope='global'` (default) → all members
 *   • `scope='city'` + `cityFilter='me'|'Tulum'|…` → city-filtered ranking
 *
 * Tokens:
 *   • Canvas bg / White cards / Teal rank / Golden points / Ocean city
 *   • Self row: subtle Teal tint + Pacific left border
 */
const LeaderboardList = ({
  limit = 20,
  dense = false,
  onRowClick,
  scope = 'global',
  cityFilter = null,
  window: timeWindow = 'all',
  onViewerRow,
}) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const params = new URLSearchParams({ limit: String(limit) });
        if (scope === 'city' && cityFilter) {
          params.set('city', cityFilter);
        }
        if (timeWindow && timeWindow !== 'all') {
          params.set('window', timeWindow);
        }
        const r = await fetch(
          `${API_URL}/api/community/leaderboard?${params.toString()}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: 'include',
          },
        );
        if (!r.ok) throw new Error('Could not load leaderboard');
        const d = await r.json();
        setData(d);
        if (typeof onViewerRow === 'function') {
          onViewerRow(d.viewer_row || null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [limit, scope, cityFilter, timeWindow, onViewerRow]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6" data-testid="leaderboard-loading">
        <Loader2 className="w-5 h-5 text-[#2CA8A0] animate-spin" />
      </div>
    );
  }
  if (error) {
    return <div className="text-[#dc2626] text-sm p-3">{error}</div>;
  }
  if (!data) return null;

  if (data.top.length === 0) {
    return (
      <div
        className="p-6 text-center text-[#5A6B73] text-sm"
        data-testid="leaderboard-empty"
      >
        {scope === 'city'
          ? `No members ranked in ${data.city || 'your city'} yet. Be the first.`
          : 'No members ranked yet.'}
      </div>
    );
  }

  const renderRow = (row, highlightOverride = false) => {
    const isMe = row.user_id === user?.id;
    const highlight = isMe || highlightOverride;
    const initial = (row.first_name || '?')[0]?.toUpperCase();
    const content = (
      <div
        className={`flex items-center gap-3 py-3 px-4 transition ${
          highlight
            ? 'bg-[#2CA8A0]/10 border-l-2 border-[#1A5C6E]'
            : 'bg-white hover:bg-[#F9F6EF] border-l-2 border-transparent'
        }`}
        data-testid={`leaderboard-row-${row.rank}`}
        data-is-me={isMe ? 'true' : 'false'}
      >
        <span className="text-sm w-7 tabular-nums shrink-0 font-mono font-semibold text-[#2CA8A0]">
          {row.rank}
        </span>
        {row.profile_photo_url ? (
          <img
            src={row.profile_photo_url}
            alt=""
            className="rounded-full object-cover shrink-0"
            style={{ width: 36, height: 36 }}
          />
        ) : (
          <div
            className="rounded-full bg-[#2CA8A0] text-white font-semibold flex items-center justify-center shrink-0"
            style={{ width: 36, height: 36, fontSize: 14 }}
          >
            {initial}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate text-[#0A1F2E] font-medium">
            {row.first_name}
            {row.last_name_initial ? ` ${row.last_name_initial}.` : ''}
          </div>
          {row.destination_city && !dense && (
            <div className="text-[#5A6B73] text-xs truncate flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" /> {row.destination_city}
            </div>
          )}
        </div>
        <span className="text-sm tabular-nums shrink-0 font-semibold text-[#E8A830]">
          {row.points}
        </span>
      </div>
    );
    return (
      <li key={row.rank + '-' + row.user_id}>
        {onRowClick === false ? (
          content
        ) : (
          <Link to={`/portal/members/${row.user_id}`} className="block">
            {content}
          </Link>
        )}
      </li>
    );
  };

  const pinViewer = !data.viewer_in_top && data.viewer_row && data.viewer_rank;

  return (
    <ul className="divide-y divide-[#EFE6CF]" data-testid="leaderboard-list">
      {data.top.map((row) => renderRow(row))}
      {pinViewer && (
        <>
          <li className="py-1">
            <div className="border-t border-dashed border-[#EFE6CF] my-1" />
          </li>
          {renderRow(data.viewer_row, true)}
        </>
      )}
    </ul>
  );
};

export default LeaderboardList;
